import React, { useMemo, useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import SportIcon from '../common/SportIcon';
import { getSportLabel } from '../../constants/sportTheme';
import { buildChallengeTicketShareText, buildChallengeTicketUrl } from '../../utils/challengeTicketIntent';
import {
  CHALLENGE_TICKET_SHARE_TITLE,
  copyTextToClipboard,
  shareChallengeTicket,
} from '../../utils/challengeTicketShareActions';
import {
  TicketStatus,
  effectiveTicketStatus,
  ticketStatusLabel,
} from '../../utils/ticketStatus';

function formatExpires(expiresAt) {
  if (!expiresAt) return null;
  try {
    const d = new Date(expiresAt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_) {
    return null;
  }
}

export default function SentChallengeTicketCard({
  ticket,
  creatorName,
  cancelBusy = false,
  onCancel,
  onViewChallenge,
}) {
  const [copied, setCopied] = useState(false);
  const status = effectiveTicketStatus(ticket) ?? TicketStatus.OPEN;
  const isOpen = status === TicketStatus.OPEN;
  const url = useMemo(
    () => (ticket?.ticketCode ? buildChallengeTicketUrl(ticket.ticketCode) : ''),
    [ticket?.ticketCode],
  );
  const shareText = useMemo(() => {
    if (!ticket) return '';
    return buildChallengeTicketShareText({
      creatorName: creatorName || 'You',
      teamA: ticket.teamA,
      teamB: ticket.teamB,
      amount: ticket.amount,
      url,
    });
  }, [ticket, creatorName, url]);

  const copyLink = async () => {
    await copyTextToClipboard(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const shareTicket = async () => {
    const result = await shareChallengeTicket({
      title: CHALLENGE_TICKET_SHARE_TITLE,
      text: shareText,
      url,
    });
    if (result === 'unsupported') await copyTextToClipboard(shareText);
  };

  const expiresLabel = formatExpires(ticket?.expiresAt);

  return (
    <Card variant="subtle" padding="sm" className="rounded-xl border-cyan-500/25 bg-cyan-500/[0.04] space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-cyan-300/90">
            Challenge Ticket · {ticketStatusLabel(status)}
          </p>
          <p className="text-sm font-black text-white mt-0.5 truncate">
            {ticket.teamA} vs {ticket.teamB}
          </p>
          <p className="text-[10px] text-gray-500 font-bold mt-0.5 inline-flex items-center gap-1 flex-wrap">
            <SportIcon sport={ticket.sport} size="xs" decorative />
            <span>
              {getSportLabel(ticket.sport)}
              {' · $'}
              {Number(ticket.amount || 0).toFixed(0)}
              {' per player'}
              {ticket.creatorPick ? ` · Your side: ${ticket.creatorPick}` : ''}
            </span>
          </p>
          {expiresLabel && isOpen && (
            <p className="text-[10px] text-amber-400/90 font-bold mt-1">Expires {expiresLabel}</p>
          )}
          <p className="text-[10px] text-gray-600 font-mono mt-1">{ticket.ticketCode}</p>
        </div>
      </div>

      {isOpen && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" className="text-[10px]" onClick={copyLink}>
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>
          <Button type="button" variant="secondary" size="sm" className="text-[10px]" onClick={shareTicket}>
            Share
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-[10px] text-red-300"
            disabled={cancelBusy}
            onClick={() => onCancel?.(ticket)}
          >
            {cancelBusy ? 'Canceling…' : 'Cancel Ticket'}
          </Button>
        </div>
      )}

      {status === TicketStatus.ACCEPTED && (
        <div className="space-y-1">
          <p className="text-[10px] text-emerald-400/90 font-bold">
            Accepted — now an active challenge.
          </p>
          {ticket.challengeId && (
            <Button type="button" variant="admin" size="sm" className="text-[10px]" onClick={() => onViewChallenge?.(ticket)}>
              View Challenge
            </Button>
          )}
        </div>
      )}

      {(status === TicketStatus.EXPIRED
        || status === TicketStatus.CANCELED
        || status === TicketStatus.DECLINED) && (
        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">
          {ticketStatusLabel(status)} · Closed
        </p>
      )}
    </Card>
  );
}
